import React, { useState, useEffect } from 'react';
import { RiUserLine, RiBuildingLine, RiMailLine } from '@remixicon/react';
import { cn } from '../../../../components/ui/utils';
import { Checkbox } from '../../../../components/ui/Checkbox';
import PhoneInput from './PhoneInput';

/**
 * Client Form Component
 * Form for entering new client data
 *
 * @param {Object} props
 * @param {Object} props.data - Form values object
 * @param {Function} props.onChange - Callback when form changes (receives full data object)
 * @param {Object} props.errors - Validation errors object
 * @param {boolean} props.disabled - Whether form is disabled
 */
const ClientForm = ({
  data = {},
  onChange,
  errors = {},
  disabled = false
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    saveForFuture: false,
    ...data
  });

  // Update form data when data prop changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      ...data
    }));
  }, [data]);

  // Handle field change
  const handleFieldChange = (name, val) => {
    const newFormData = {
      ...formData,
      [name]: val
    };
    setFormData(newFormData);
    onChange?.(newFormData);
  };

  // Trim and process value
  const processValue = (value, type) => {
    if (!value) return '';
    if (type === 'name' || type === 'company') {
      return value.trim();
    }
    if (type === 'email') {
      return value.toLowerCase().trim();
    }
    return value;
  };

  // Handle input change with trimming
  const handleInputChange = (name, value, type) => {
    const processedValue = type ? processValue(value, type) : value;
    handleFieldChange(name, processedValue);
  };

  return (
    <div className="space-y-4">
      {/* Two-column grid for First Name and Last Name */}
      <div className="grid grid-cols-2 gap-4">
        {/* First Name */}
        <div>
          <label htmlFor="client-first-name" className="block text-xs font-medium text-gray-600 mb-1.5">
            First Name *
          </label>
          <div className="relative">
            <div
              className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              data-testid="user-icon"
            >
              <RiUserLine className="w-4 h-4 text-gray-400" />
            </div>
            <input
              id="client-first-name"
              type="text"
              value={formData.firstName || ''}
              onChange={(e) => handleInputChange('firstName', e.target.value, 'name')}
              placeholder="Sarah"
              disabled={disabled}
              aria-required="true"
              aria-invalid={errors.firstName ? 'true' : 'false'}
              aria-describedby={errors.firstName ? 'client-first-name-error' : undefined}
              autoComplete="given-name"
              className={cn(
                'w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm transition-all',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                errors.firstName && 'error border-red-500 focus:ring-red-500',
                disabled && 'disabled bg-gray-100 cursor-not-allowed opacity-60',
                !errors.firstName && !disabled && 'border-gray-300 hover:border-gray-400'
              )}
            />
          </div>
          {errors.firstName && (
            <div id="client-first-name-error" className="mt-1 text-xs text-red-600">
              {errors.firstName}
            </div>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label htmlFor="client-last-name" className="block text-xs font-medium text-gray-600 mb-1.5">
            Last Name *
          </label>
          <div className="relative">
            <div
              className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            >
              <RiUserLine className="w-4 h-4 text-gray-400" />
            </div>
            <input
              id="client-last-name"
              type="text"
              value={formData.lastName || ''}
              onChange={(e) => handleInputChange('lastName', e.target.value, 'name')}
              placeholder="Mitchell"
              disabled={disabled}
              aria-required="true"
              aria-invalid={errors.lastName ? 'true' : 'false'}
              aria-describedby={errors.lastName ? 'client-last-name-error' : undefined}
              autoComplete="family-name"
              className={cn(
                'w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm transition-all',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                errors.lastName && 'error border-red-500 focus:ring-red-500',
                disabled && 'disabled bg-gray-100 cursor-not-allowed opacity-60',
                !errors.lastName && !disabled && 'border-gray-300 hover:border-gray-400'
              )}
            />
          </div>
          {errors.lastName && (
            <div id="client-last-name-error" className="mt-1 text-xs text-red-600">
              {errors.lastName}
            </div>
          )}
        </div>
      </div>

      {/* Two-column grid for Company Name and Email */}
      <div className="grid grid-cols-2 gap-4">
        {/* Company Name */}
        <div>
          <label htmlFor="client-company" className="block text-xs font-medium text-gray-600 mb-1.5">
            Company Name
          </label>
          <div className="relative">
            <div
              className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              data-testid="building-icon"
            >
              <RiBuildingLine className="w-4 h-4 text-gray-400" />
            </div>
            <input
              id="client-company"
              type="text"
              value={formData.company || ''}
              onChange={(e) => handleInputChange('company', e.target.value, 'company')}
              placeholder="Mitchell & Associates"
              disabled={disabled}
              aria-invalid={errors.company ? 'true' : 'false'}
              aria-describedby={errors.company ? 'client-company-error' : undefined}
              autoComplete="organization"
              className={cn(
                'w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm transition-all',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                errors.company && 'error border-red-500 focus:ring-red-500',
                disabled && 'disabled bg-gray-100 cursor-not-allowed opacity-60',
                !errors.company && !disabled && 'border-gray-300 hover:border-gray-400'
              )}
            />
          </div>
          {errors.company && (
            <div id="client-company-error" className="mt-1 text-xs text-red-600">
              {errors.company}
            </div>
          )}
        </div>

        {/* Email Address */}
        <div>
          <label htmlFor="client-email" className="block text-xs font-medium text-gray-600 mb-1.5">
            Email Address *
          </label>
          <div className="relative">
            <div
              className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              data-testid="envelope-icon"
            >
              <RiMailLine className="w-4 h-4 text-gray-400" />
            </div>
            <input
              id="client-email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value, 'email')}
              placeholder="sarah-mitchell@mitchell-assoc.com"
              disabled={disabled}
              aria-required="true"
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'client-email-error' : undefined}
              autoComplete="email"
              className={cn(
                'w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm transition-all',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                errors.email && 'error border-red-500 focus:ring-red-500',
                disabled && 'disabled bg-gray-100 cursor-not-allowed opacity-60',
                !errors.email && !disabled && 'border-gray-300 hover:border-gray-400'
              )}
            />
          </div>
          {errors.email && (
            <div id="client-email-error" className="mt-1 text-xs text-red-600">
              {errors.email}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Number */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="client-phone" className="block text-xs font-medium text-gray-600 mb-1.5">
            Mobile Number *
          </label>
          <div data-testid="phone-icon">
            <PhoneInput
              value={formData.phone || ''}
              countryCode="+1"
              onChange={({ phone }) => handleFieldChange('phone', phone)}
              error={errors.phone || ''}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/* Address */}
      <div>
        <label htmlFor="client-address1" className="block text-xs font-medium text-gray-600 mb-1.5">
          Address *
        </label>
        <input
          id="client-address1"
          type="text"
          value={formData.address1 || ''}
          onChange={(e) => handleFieldChange('address1', e.target.value)}
          placeholder="123 East 85th Street, Apt 5G"
          disabled={disabled}
          aria-required="true"
          autoComplete="street-address"
          className={cn(
            'w-full px-4 py-2.5 border rounded-lg text-sm transition-all',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            errors.address1 && 'error border-red-500 focus:ring-red-500',
            disabled && 'disabled bg-gray-100 cursor-not-allowed opacity-60',
            !errors.address1 && !disabled && 'border-gray-300 hover:border-gray-400'
          )}
        />
        {errors.address1 && (
          <div className="mt-1 text-xs text-red-600">{errors.address1}</div>
        )}
      </div>

      {/* City, State, Zip */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label htmlFor="client-city" className="block text-xs font-medium text-gray-600 mb-1.5">
            City *
          </label>
          <input
            id="client-city"
            type="text"
            value={formData.city || ''}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            placeholder="New York City"
            disabled={disabled}
            aria-required="true"
            autoComplete="address-level2"
            className={cn(
              'w-full px-4 py-2.5 border rounded-lg text-sm transition-all',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
              errors.city && 'error border-red-500 focus:ring-red-500',
              disabled && 'disabled bg-gray-100 cursor-not-allowed opacity-60',
              !errors.city && !disabled && 'border-gray-300 hover:border-gray-400'
            )}
          />
          {errors.city && (
            <div className="mt-1 text-xs text-red-600">{errors.city}</div>
          )}
        </div>

        <div>
          <label htmlFor="client-state" className="block text-xs font-medium text-gray-600 mb-1.5">
            State *
          </label>
          <input
            id="client-state"
            type="text"
            value={formData.state || ''}
            onChange={(e) => handleFieldChange('state', e.target.value)}
            placeholder="New York"
            disabled={disabled}
            aria-required="true"
            autoComplete="address-level1"
            className={cn(
              'w-full px-4 py-2.5 border rounded-lg text-sm transition-all',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
              errors.state && 'error border-red-500 focus:ring-red-500',
              disabled && 'disabled bg-gray-100 cursor-not-allowed opacity-60',
              !errors.state && !disabled && 'border-gray-300 hover:border-gray-400'
            )}
          />
          {errors.state && (
            <div className="mt-1 text-xs text-red-600">{errors.state}</div>
          )}
        </div>

        <div>
          <label htmlFor="client-zip" className="block text-xs font-medium text-gray-600 mb-1.5">
            Zip Code *
          </label>
          <input
            id="client-zip"
            type="text"
            value={formData.zip || ''}
            onChange={(e) => handleFieldChange('zip', e.target.value)}
            placeholder="10028"
            disabled={disabled}
            aria-required="true"
            autoComplete="postal-code"
            className={cn(
              'w-full px-4 py-2.5 border rounded-lg text-sm transition-all',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
              errors.zip && 'error border-red-500 focus:ring-red-500',
              disabled && 'disabled bg-gray-100 cursor-not-allowed opacity-60',
              !errors.zip && !disabled && 'border-gray-300 hover:border-gray-400'
            )}
          />
          {errors.zip && (
            <div className="mt-1 text-xs text-red-600">{errors.zip}</div>
          )}
        </div>
      </div>

      {/* Save for future checkbox */}
      <div className="flex items-center gap-2 pt-2">
        <Checkbox
          id="save-client"
          checked={formData.saveForFuture || false}
          onCheckedChange={(checked) => handleFieldChange('saveForFuture', checked)}
          disabled={disabled}
          role="checkbox"
        />
        <label
          htmlFor="save-client"
          className={cn(
            'text-sm text-gray-700 cursor-pointer select-none',
            disabled && 'opacity-60 cursor-not-allowed'
          )}
        >
          Save client information for future use
        </label>
      </div>
    </div>
  );
};

export default ClientForm;
